// SingleDocUpload.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';

function SingleDocUpload({ onClose, onUploadSuccess, isModal = false }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [lessonNumber, setLessonNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState('');
  const [isError, setIsError] = useState(false);

  // Use unique IDs for inputs if in modal to avoid conflicts
  const lessonNumberInputId = isModal ? 'lessonNumber-modal' : 'lessonNumber-page';
  const fileInputId = isModal ? 'file-input-modal' : 'file-input-page';

  useEffect(() => {
    // Attempt to clear the file input visually when selectedFile is reset
    if (!selectedFile && document.getElementById(fileInputId)) {
      document.getElementById(fileInputId).value = '';
    }
  }, [selectedFile, fileInputId]); // Add fileInputId to dependencies

  const handleFileChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    } else {
      setSelectedFile(null);
    }
    setUploadMessage('');
    setIsError(false);
  };

  const handleLessonNumberChange = (event) => {
    setLessonNumber(event.target.value);
    setUploadMessage('');
    setIsError(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedFile || !lessonNumber.trim()) {
      setUploadMessage('Please select a file and enter a lesson number.');
      setIsError(true);
      return;
    }

    setIsLoading(true);
    setUploadMessage('');
    setIsError(false);

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('lessonNumber', lessonNumber.trim());


    
    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/v1/admin/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      console.log("🚀 ~ handleSubmit ~ response:", response)
      setUploadMessage(response.data.message || 'File uploaded successfully!');
      setIsError(false);
      setSelectedFile(null); // Triggers useEffect to clear file input
      setLessonNumber('');
      
      if (onUploadSuccess) {
        onUploadSuccess(); // Notify parent
      }
      // Optionally auto-close modal after a short delay
      if (isModal && onClose) {
        setTimeout(() => {
            onClose();
        }, 1500); // Close after 1.5 seconds so user can see message
      }

    } catch (error) {
      let errorMessage = 'An error occurred during upload.';
    if (error.response?.data?.error) {
  errorMessage = error.response.data.error;
} else if (error.response?.data?.message) {
  errorMessage = error.response.data.message;
} else if (error.message) {
  errorMessage = error.message;
}
      setUploadMessage(errorMessage);
      setIsError(true);
      console.error('Upload error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const wrapperClass = isModal
    ? "font-sans" // Modal content doesn't need its own bg or min-height
    : "p-5 md:p-8 font-sans min-h-screen bg-gray-100";

  const formContainerClass = isModal
    ? "p-6 md:p-8" // Padding for modal content
    : "max-w-xl mx-auto bg-white p-6 md:p-8 rounded-lg shadow-xl";

  return (
    <div className={wrapperClass}>
      {!isModal && (
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 mb-6 border-b-2 border-blue-600 pb-3">
          Upload Document
        </h2>
      )}

      {isModal && (
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-xl font-semibold text-gray-800">Upload New Document</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
            aria-label="Close modal"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path></svg>
          </button>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className={formContainerClass}
      >
        <div className="mb-6">
          <label
            htmlFor={lessonNumberInputId}
            className="block mb-2 text-sm font-medium text-gray-700"
          >
            Lesson Number:
          </label>
          <input
            type="text"
            id={lessonNumberInputId}
            value={lessonNumber}
            onChange={handleLessonNumberChange}
            placeholder="Enter the Lesson Number"
            className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-base"
            required
          />
        </div>

        <div className="mb-6">
          <label
            htmlFor={fileInputId}
            className="block mb-2 text-sm font-medium text-gray-700"
          >
            Select Document:
          </label>
          <input
            type="file"
            id={fileInputId}
            onChange={handleFileChange}
            className="block w-full text-sm text-gray-700 border border-gray-300 rounded-md cursor-pointer
                       file:mr-4 file:py-2 file:px-4
                       file:rounded-l-md file:border-0
                       file:text-sm file:font-semibold
                       file:bg-gray-100 file:text-gray-800
                       hover:file:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          />
          {selectedFile && (
            <p className="mt-2 text-xs text-gray-600">
              Selected: {selectedFile.name}
            </p>
          )}
        </div>

        <div className={`flex ${isModal ? 'justify-end space-x-3' : 'justify-start'}`}>
          {isModal && (
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors text-base"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={isLoading || !selectedFile || !lessonNumber.trim()}
            className={`py-3 px-5 bg-blue-600 text-white font-medium rounded-md text-base
                       hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                       disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors duration-200 ease-in-out
                       ${isModal ? '' : 'w-full'}`} // Full width only if not modal
          >
            {isLoading ? 'Uploading...' : 'Upload Document'}
          </button>
        </div>

        {uploadMessage && (
          <div
            className={`mt-5 p-3 rounded-md text-center text-sm ${
              isError
                ? 'bg-red-100 text-red-700 border border-red-300'
                : 'bg-green-100 text-green-700 border border-green-300'
            }`}
          >
            {uploadMessage}
          </div>
        )}
      </form>
    </div>
  );
}

export default SingleDocUpload;